import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import Header from './components/Header';
import WorkoutPage from './components/WorkoutPage';
import Footer from './components/Footer';
import Analytics from './components/Analytics';
import Exercises from './components/Exercises';
import Settings from './components/Settings';
import Auth from './components/Auth';
import AdminPage from './components/AdminPage';
import ProtectedRoute from './ProtectedRoute';
import Home from './components/Home';
import Spinner from './components/Spinner';
import { getWorkouts, addWorkout, refreshAuthToken, loginWithTelegram, getCustomMuscleGroups, saveCustomMuscleGroups, getUserInfo } from './api';
import './global.css';

const defaultMuscleGroups = {
  "Грудные": [
    "Жим штанги лежа (горизонтальный)",
    "Жим штанги лежа (наклон вверх)",
    "Жим штанги лежа (наклон вниз)",
    "Жим гантелей лежа (горизонтальный)",
    "Жим гантелей лежа (наклон вверх)",
    "Жим гантелей лежа (наклон 45°)",
    "Разводка гантелей лежа (горизонтальный)",
    "Разводка гантелей лежа (наклон вверх)",
    "Сведение рук в кроссовере",
    "Бабочка",
    "Отжимания",
    "Отжимания на брусьях"
  ],
  "Спина": [
    "Тяга верхнего блока к груди",
    "Тяга горизонтального блока",
    "Тяга штанги в наклоне",
    "Тяга гантели одной рукой в наклоне",
    "Тяга блока одной рукой",
    "Становая тяга",
    "Подтягивания широким хватом",
    "Подтягивания обратным хватом"
  ],
  "Ноги": [
    "Приседания со штангой",
    "Жим ногами в тренажере",
    "Разгибание ног в тренажере",
    "Сгибание ног в тренажере (лежа)",
    "Сгибание ног в тренажере (стоя)",
    "Выпады с гантелями",
    "Приседания с гантелями"
  ],
"Руки": [
    "Подъем гантелей на бицепс (сидя)",
    "Подъем гантелей на бицепс (стоя)",
    "Подъем EZ-грифа на бицепс (стоя)",
    "Подъем штанги на бицепс (стоя)",
    "Сгибание рук на скамье Скотта",
    "Молотковый подъем гантелей (стоя)",
    "Молотковый подъем гантелей (сидя)",
    "Жим штанги узким хватом",
    "Французский жим со штангой",
    "Разгибание рук с гантелью из-за головы (двумя руками)",
    "Разгибание рук с гантелью из-за головы (одной рукой)",
    "Разгибание рук (смит)",
    "Разгибание рук с гантелями в наклоне"
  ],
  "Плечи": [
    "Жим штанги сидя",
    "Жим гантелей сидя",
    "Подъем гантелей перед собой",
    "Подъем гантелей в стороны стоя",
    "Разводка гантелей в наклоне (задняя дельта)",
    "Тяга штанги к подбородку",
    "Шраги с гантелями (трапеция)"
  ],
  "Пресс и стабилизаторы": [
    "Подъем ног в висе на турнике",
    "Подъем ног на брусьях",
    "Скручивания на полу",
    "Планка (30 сек)",
    "Планка (1 минута)",
    "Гиперэкстензия"
  ],
  "Икры": [
    "Подъемы на носки стоя со штангой",
    "Подъемы на носки сидя в тренажере",
    "Подъемы на носки в тренажере стоя"
  ]
};


const App = () => {
  const [darkMode, setDarkMode] = useState(() => {
    const savedDarkMode = localStorage.getItem('darkMode');
    return savedDarkMode !== null ? savedDarkMode === 'true' : true;
  });
  const [selectedDate, setSelectedDate] = useState(() => new Date(localStorage.getItem('selectedDate') || Date.now()));
  const [showTable, setShowTable] = useState(() => localStorage.getItem('showTable') === 'true');
  const [workoutData, setWorkoutData] = useState({});
  const [isAuthenticated, setIsAuthenticated] = useState(() => !!localStorage.getItem('jwt'));
  const [authToken, setAuthToken] = useState(localStorage.getItem('jwt') || null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [customMuscleGroups, setCustomMuscleGroups] = useState({});
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const hasInitialized = useRef(false);
  const lastNavigation = useRef({ date: null, workoutId: null });

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    /* $& */
    window.scrollTo(0, 0);
    lastNavigation.current = { date: null, workoutId: null };
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      sessionStorage.setItem('scrollPosition', window.scrollY);
    };

    const restoreScrollPosition = () => {
      const scrollPosition = sessionStorage.getItem('scrollPosition');
      if (scrollPosition) {
        window.scrollTo(0, parseInt(scrollPosition, 10));
      }
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('load', restoreScrollPosition);
    restoreScrollPosition();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('load', restoreScrollPosition);
    };
  }, []);

  useEffect(() => {
    const initializeAuth = async () => {
      if (hasInitialized.current) {
        /* $& */
        return;
      }
      hasInitialized.current = true;
  
      /* $& */
      setLoading(true);
      try {
        if (window.Telegram?.WebApp?.initDataUnsafe?.user && !isAuthenticated) {
          /* $& */
          const token = await loginWithTelegram(window.Telegram.WebApp.initDataUnsafe.user);
          localStorage.setItem('jwt', token);
          setAuthToken(token);
          setIsAuthenticated(true);
          await fetchData(token);
          const userInfo = await getUserInfo(token);
          setIsAdmin(userInfo.isAdmin || false);
          /* $& */
        } else if (authToken && !isTokenExpired(authToken)) {
          /* $& */
          setIsAuthenticated(true);
          await fetchData(authToken); // Fixed: Use authToken instead of token
          const userInfo = await getUserInfo(authToken); // Fixed: Use authToken instead of token
          setIsAdmin(userInfo.isAdmin || false);
        } else {
          if (document.hasStorageAccess && !(await document.hasStorageAccess())) {
            /* $& */
            await document.requestStorageAccess();
          }
  
          const checkCookie = await fetch(`${process.env.REACT_APP_API_URL}/check-cookie`, {
            method: 'GET',
            credentials: 'include',
          });
          if (checkCookie.ok) {
            /* $& */
            const newToken = await refreshAuthToken();
            if (newToken) {
              /* $& */
              localStorage.setItem('jwt', newToken);
              setAuthToken(newToken);
              setIsAuthenticated(true);
              await fetchData(newToken);
              const userInfo = await getUserInfo(newToken);
              setIsAdmin(userInfo.isAdmin || false);
            } else {
              /* $& */
              setMessage('Пожалуйста, войдите в аккаунт.');
              setMessageType('info');
              setIsAuthenticated(false);
              localStorage.removeItem('jwt');
              navigate('/auth');
            }
          } else {
            /* $& */
            setIsAuthenticated(false);
            localStorage.removeItem('jwt');
            navigate('/auth');
          }
        }
      } catch (error) {
        setMessage('Ошибка авторизации. Попробуйте снова.');
        setMessageType('error');
        setIsAuthenticated(false);
        localStorage.removeItem('jwt');
        navigate('/auth');
      } finally {
        /* $& */
        setLoading(false);
      }
    };
  
    initializeAuth();
  }, [navigate, authToken, isAuthenticated]);

  useEffect(() => {
    if (darkMode) document.body.classList.add('dark-theme');
    else document.body.classList.remove('dark-theme');
    localStorage.setItem('darkMode', darkMode);
  }, [darkMode]);

  useEffect(() => {
    localStorage.setItem('selectedDate', selectedDate.toISOString());
    localStorage.setItem('showTable', showTable);
  }, [selectedDate, showTable]);

  const fetchData = async (token) => {
    /* $& */
    try {
      const [workoutsResponse, muscleGroupsResponse] = await Promise.all([
        getWorkouts(token),
        getCustomMuscleGroups(token),
      ]);
      const formattedData = workoutsResponse.workouts.reduce((acc, workout) => {
        const dateKey = new Date(workout.workout_date).toDateString();
        if (!acc[dateKey]) acc[dateKey] = [];
        acc[dateKey].push({
          workoutId: workout.workoutId,
          title: workout.title || '',
          exercises: workout.exercises || [],
          bodyWeight: workout.bodyWeight || null,
          notes: workout.notes || '',
        });
        return acc;
      }, {});
      setWorkoutData(formattedData);
      setCustomMuscleGroups(muscleGroupsResponse.muscleGroups || {});
      /* $& */
    } catch (error) {
    
      if (error.message.includes('Unauthorized')) handleLogout();
    }
  };

  const formatDateToLocal = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleDateSelect = (date, workoutId = 1) => {
    if (!(date instanceof Date) || isNaN(date)) {
      
      return;
    }
    const dateKey = date.toDateString();
    const navigationKey = `${dateKey}-${workoutId}`;
    if (
      lastNavigation.current.date === dateKey &&
      lastNavigation.current.workoutId === workoutId
    ) {
      /* $& */
      return;
    }
    lastNavigation.current = { date: dateKey, workoutId };
    setSelectedDate(date);
    setShowTable(true);
    const formattedDate = formatDateToLocal(date);
    /* $& */
    navigate(`/${formattedDate}/${workoutId}`);
  };

  const handleLogin = async (token) => {
    /* $& */
    localStorage.setItem('jwt', token);
    setAuthToken(token);
    setIsAuthenticated(true);
    setSelectedDate(new Date());
    await fetchData(token);
    try {
      const userInfo = await getUserInfo(token);
      setIsAdmin(userInfo.isAdmin || false);
    } catch (error) {
     
    }
  };

  const handleWorkoutChange = async (dataForDate, workoutId, bodyWeight, notes) => {
    try {
      const workoutDate = formatDateToLocal(selectedDate);
      const currentWorkouts = workoutData[selectedDate.toDateString()] || [];
      const currentWorkout = currentWorkouts.find((w) => w.workoutId === workoutId) || {};
      const updatedWorkout = {
        workout_date: workoutDate,
        workoutId: workoutId || currentWorkouts.length + 1,
        title: currentWorkout.title || '',
        exercises: dataForDate.map((workout, index) => ({
          ...workout,
          number: workout.number || index + 1,
        })),
        bodyWeight: bodyWeight || null,
        notes: notes || '',
      };
      setWorkoutData((prevData) => ({
        ...prevData,
        [selectedDate.toDateString()]: [
          ...(prevData[selectedDate.toDateString()] || []).filter((w) => w.workoutId !== workoutId),
          updatedWorkout,
        ].sort((a, b) => a.workoutId - b.workoutId),
      }));
      await addWorkout(updatedWorkout, authToken);
    } catch (error) {
      
      setMessage('Ошибка при сохранении тренировки.');
      setMessageType('error');
    }
  };

  const handleTitleChange = async (newTitle, workoutId) => {
    try {
      const workoutDate = formatDateToLocal(selectedDate);
      const currentWorkouts = workoutData[selectedDate.toDateString()] || [];
      const currentWorkout = currentWorkouts.find((w) => w.workoutId === workoutId) || {};
      const updatedWorkout = {
        workout_date: workoutDate,
        workoutId,
        title: newTitle,
        exercises: currentWorkout.exercises || [],
        bodyWeight: currentWorkout.bodyWeight || null,
        notes: currentWorkout.notes || '',
      };
      setWorkoutData((prevData) => ({
        ...prevData,
        [selectedDate.toDateString()]: [
          ...(prevData[selectedDate.toDateString()] || []).filter((w) => w.workoutId !== workoutId),
          updatedWorkout,
        ].sort((a, b) => a.workoutId - b.workoutId),
      }));
      await addWorkout(updatedWorkout, authToken);
    } catch (error) {
    
      setMessage('Ошибка при сохранении названия тренировки.');
      setMessageType('error');
    }
  };

  const handleMuscleGroupsChange = async (newCustomGroups) => {
    try {
      setCustomMuscleGroups(newCustomGroups);
      await saveCustomMuscleGroups(newCustomGroups, authToken);
      await fetchData(authToken);
    } catch (error) {
  
      setMessage('Ошибка при сохранении групп мышц.');
      setMessageType('error');
    }
  };

  const toggleTheme = () => setDarkMode((prev) => !prev);

  const handleLogout = async () => {
    /* $& */
    try {
      await fetch(`${process.env.REACT_APP_API_URL}/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
     
    }
    localStorage.removeItem('jwt');
    setAuthToken(null);
    setWorkoutData({});
    setCustomMuscleGroups({});
    setIsAuthenticated(false);
    setIsAdmin(false);
    hasInitialized.current = false;
    navigate('/auth');
  };

  const isTokenExpired = (token) => {
    try {
      const decoded = jwtDecode(token);
      return decoded.exp * 1000 < Date.now();
    } catch (error) {
      return true;
    }
  };

  return (
    <div className="app">
      <Header
        darkMode={darkMode}
        toggleTheme={toggleTheme}
        onLogout={handleLogout}
        showLogoutButton={location.pathname !== '/auth'}
        isAdmin={isAdmin}
      />
      {loading ? (
        <Spinner darkMode={darkMode} />
      ) : (
        <main className="container page-transition" key={location.pathname}>
          <Routes location={location}>
            <Route path="/auth" element={<Auth onLogin={handleLogin} darkMode={darkMode} />} />
            <Route
              path="/:date/:workoutId"
              element={
                <ProtectedRoute>
                  <WorkoutPage
                    workoutData={workoutData}
                    selectedDate={selectedDate}
                    onDateSelect={handleDateSelect}
                    onWorkoutChange={handleWorkoutChange}
                    onTitleChange={handleTitleChange}
                    loading={loading}
                    darkMode={darkMode}
                    defaultMuscleGroups={defaultMuscleGroups}
                    customMuscleGroups={customMuscleGroups}
                    authToken={authToken}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/home"
              element={
                <ProtectedRoute>
                  <Home
                    workoutData={workoutData}
                    onDateSelect={handleDateSelect}
                    darkMode={darkMode}
                    loading={loading}
                    authToken={authToken}
                    onDataUpdate={() => fetchData(authToken)}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/analytics"
              element={
                <ProtectedRoute>
                  <Analytics workoutData={workoutData} darkMode={darkMode} loading={loading} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/exercises"
              element={
                <ProtectedRoute>
                  <Exercises
                    darkMode={darkMode}
                    defaultMuscleGroups={defaultMuscleGroups}
                    customMuscleGroups={customMuscleGroups}
                    onMuscleGroupsChange={handleMuscleGroupsChange}
                    loading={loading}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Settings
                    darkMode={darkMode}
                    toggleTheme={toggleTheme}
                    onLogout={handleLogout}
                    loading={loading}
                    authToken={authToken}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminPage darkMode={darkMode} authToken={authToken} />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to="/home" />} />
          </Routes>
        </main>
      )}
      {location.pathname !== '/auth' && !loading && (
        <Footer darkMode={darkMode} onNavigateToday={() => handleDateSelect(new Date())} />
      )}
    </div>
  );
};

export default App;